import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosConfig";
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Stack
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getCateringLabel, getOvernightLabel } from "../../utils/labels";

//import { handleApiError } from "../../utils/apiErrorHandler";

interface TShirtSummary {
  standard: Record<string, number>;
  individualized: Record<string, number>;
}

interface CateringSummary {
  [key: string]: number;
}

interface OvernightSummary {
  [key: string]: number;
}

interface ClubBooking {
  clubId: string;
  clubName: string;
  tShirtBooking: {
    individualizable: boolean;
    customText?: string;
    sizes: Record<string, number>;
  };
  cateringBooking: {
    dinnerFriday: number;
    dinnerFridayVeg: number;
    breakfastSaturday: number;
    breakfastSunday: number;
    [key: string]: number;
  };
  overnightBooking: {
    fridayToSaturday: number;
    saturdayToSunday: number;
    [key: string]: number;
  };
}

interface CompetitionData {
  name: string;
  tShirt: {
    priceIndividualized: number;
    priceStandard: number;
  };
  catering: Record<string, { price: number }>;
  overnight: Record<string, { price: number }>;
}

interface AdditionalServicesOverviewProps {
  competitionId: string;
}

const AdditionalServicesOverview: React.FC<AdditionalServicesOverviewProps> = ({ competitionId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [clubBookings, setClubBookings] = useState<ClubBooking[]>([]);
  const [competitionData, setCompetitionData] = useState<CompetitionData | null>(null);
  
  const tShirtSizes = [
    "128", "140", "152", "164",
    "Damen S", "Damen M", "Damen L", "Damen XL",
    "Herren S", "Herren M", "Herren L", "Herren XL", "Herren XXL"
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch competition data to get pricing information
        const competitionResponse = await axios.get(`/api/competitions/${competitionId}`);
        setCompetitionData(competitionResponse.data);
        
        // Fetch the additional services summary
        const summaryResponse = await axios.get(`/api/competition-registrations/${competitionId}/additional-services`);
        setClubBookings(summaryResponse.data);
        
      } catch (err) {
        //handleApiError(err, "Error fetching additional services data.");
        setError("Fehler beim Laden der Zusatzbuchungen.");
      } finally {
        setLoading(false);
      }
    };

    if (competitionId) {
      fetchData();
    }
  }, [competitionId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // T-Shirt CSV Export
  const exportTShirtsToCSV = () => {
    const csvHeaders = [
      'Verein',
      'Größe',
      'Anzahl',
      'Individualisiert',
      'Individualisierungstext',
      'Einzelpreis',
      'Gesamtpreis'
    ];

    const csvData: string[][] = [];

    clubBookings.forEach(booking => {
      if (!booking.tShirtBooking) return;
      
      Object.entries(booking.tShirtBooking.sizes).forEach(([size, quantity]) => {
        if (quantity > 0) {
          const isIndividualized = booking.tShirtBooking.individualizable;
          const customText = booking.tShirtBooking.customText || '';
          const pricePerShirt = isIndividualized 
            ? competitionData!.tShirt.priceIndividualized 
            : competitionData!.tShirt.priceStandard;
          const totalPrice = quantity * pricePerShirt;
          
          csvData.push([
            booking.clubName,
            size,
            quantity.toString(),
            isIndividualized ? 'Ja' : 'Nein',
            customText,
            pricePerShirt.toFixed(2) + ' €',
            totalPrice.toFixed(2) + ' €'
          ]);
        }
      });
    });

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `T-Shirts_${competitionData!.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Verpflegung CSV Export
  const exportCateringToCSV = () => {
    const csvHeaders = [
      'Verein',
      'Option',
      'Anzahl',
      'Einzelpreis',
      'Gesamtpreis'
    ];

    const csvData: string[][] = [];

    clubBookings.forEach(booking => {
      if (!booking.cateringBooking) return;
      
      Object.entries(booking.cateringBooking).forEach(([option, quantity]) => {
        if (quantity > 0) {
          const price = competitionData!.catering[option]?.price || 0;
          const totalPrice = quantity * price;
          
          csvData.push([
            booking.clubName,
            getCateringLabel(option),
            quantity.toString(),
            price.toFixed(2) + ' €',
            totalPrice.toFixed(2) + ' €'
          ]);
        }
      });
    });

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Verpflegung_${competitionData!.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Übernachtung CSV Export
  const exportOvernightToCSV = () => {
    const csvHeaders = [
      'Verein',
      'Option',
      'Anzahl',
      'Einzelpreis',
      'Gesamtpreis'
    ];

    const csvData: string[][] = [];

    clubBookings.forEach(booking => {
      if (!booking.overnightBooking) return;

      Object.entries(booking.overnightBooking).forEach(([option, quantity]) => {
        if (quantity > 0) {
          const price = competitionData!.overnight[option]?.price || 0;
          const totalPrice = quantity * price;

          csvData.push([
            booking.clubName,
            getOvernightLabel(option),
            quantity.toString(),
            price.toFixed(2) + ' €',
            totalPrice.toFixed(2) + ' €'
          ]);
        }
      });
    });

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Uebernachtung_${competitionData!.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // T-Shirt Print Export
  const exportTShirtsToPrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let totalRevenue = 0;
    let totalQuantity = 0;

    const createTShirtRows = () => {
      const rows: string[] = [];
      
      clubBookings.forEach(booking => {
        if (!booking.tShirtBooking) return;
        
        Object.entries(booking.tShirtBooking.sizes).forEach(([size, quantity]) => {
          if (quantity > 0) {
            const isIndividualized = booking.tShirtBooking.individualizable;
            const customText = booking.tShirtBooking.customText || '';
            const pricePerShirt = isIndividualized 
              ? competitionData!.tShirt.priceIndividualized 
              : competitionData!.tShirt.priceStandard;
            const totalPrice = quantity * pricePerShirt;
            
            totalRevenue += totalPrice;
            totalQuantity += quantity;
            
            rows.push(`
              <tr>
                <td style="border: 1px solid #ccc; padding: 8px;">${booking.clubName}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${size}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${quantity}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${isIndividualized ? 'Ja' : 'Nein'}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${customText}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${pricePerShirt.toFixed(2)} €</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${totalPrice.toFixed(2)} €</td>
              </tr>
            `);
          }
        });
      });
      
      return rows.join('');
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>T-Shirt Buchungen - ${competitionData!.name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; border-bottom: 2px solid #1976d2; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background-color: #f5f5f5; border: 1px solid #ccc; padding: 10px; text-align: left; }
          td { border: 1px solid #ccc; padding: 8px; vertical-align: top; }
          .summary { background-color: #f9f9f9; padding: 15px; margin: 20px 0; border-left: 4px solid #1976d2; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>👕 T-Shirt Buchungen</h1>
        <p><strong>Wettkampf:</strong> ${competitionData!.name}</p>
        <p><strong>Exportiert am:</strong> ${new Date().toLocaleDateString('de-DE')}</p>
        
        <table>
          <thead>
            <tr>
              <th>Verein</th>
              <th>Größe</th>
              <th>Anzahl</th>
              <th>Individualisiert</th>
              <th>Individualisierungstext</th>
              <th>Einzelpreis</th>
              <th>Gesamtpreis</th>
            </tr>
          </thead>
          <tbody>
            ${createTShirtRows()}
          </tbody>
        </table>

        <div class="summary">
          <strong>Zusammenfassung:</strong><br>
          Gesamtanzahl T-Shirts: ${totalQuantity}<br>
          Gesamtumsatz: ${totalRevenue.toFixed(2)} €
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Verpflegung Print Export
  const exportCateringToPrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let totalRevenue = 0;
    let totalQuantity = 0;

    const createCateringRows = () => {
      const rows: string[] = [];
      
      clubBookings.forEach(booking => {
        if (!booking.cateringBooking) return;
        
        Object.entries(booking.cateringBooking).forEach(([option, quantity]) => {
          if (quantity > 0) {
            const price = competitionData!.catering[option]?.price || 0;
            const totalPrice = quantity * price;
            
            totalRevenue += totalPrice;
            totalQuantity += quantity;
            
            rows.push(`
              <tr>
                <td style="border: 1px solid #ccc; padding: 8px;">${booking.clubName}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${getCateringLabel(option)}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${quantity}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${price.toFixed(2)} €</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${totalPrice.toFixed(2)} €</td>
              </tr>
            `);
          }
        });
      });
      
      return rows.join('');
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Verpflegung Buchungen - ${competitionData!.name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; border-bottom: 2px solid #1976d2; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background-color: #f5f5f5; border: 1px solid #ccc; padding: 10px; text-align: left; }
          td { border: 1px solid #ccc; padding: 8px; vertical-align: top; }
          .summary { background-color: #f9f9f9; padding: 15px; margin: 20px 0; border-left: 4px solid #1976d2; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>🍽️ Verpflegung Buchungen</h1>
        <p><strong>Wettkampf:</strong> ${competitionData!.name}</p>
        <p><strong>Exportiert am:</strong> ${new Date().toLocaleDateString('de-DE')}</p>
        
        <table>
          <thead>
            <tr>
              <th>Verein</th>
              <th>Option</th>
              <th>Anzahl</th>
              <th>Einzelpreis</th>
              <th>Gesamtpreis</th>
            </tr>
          </thead>
          <tbody>
            ${createCateringRows()}
          </tbody>
        </table>

        <div class="summary">
          <strong>Zusammenfassung:</strong><br>
          Gesamtanzahl Verpflegung: ${totalQuantity}<br>
          Gesamtumsatz: ${totalRevenue.toFixed(2)} €
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Übernachtung Print Export
  const exportOvernightToPrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let totalRevenue = 0;
    let totalQuantity = 0;

    const createOvernightRows = () => {
      const rows: string[] = [];

      clubBookings.forEach(booking => {
        if (!booking.overnightBooking) return;

        Object.entries(booking.overnightBooking).forEach(([option, quantity]) => {
          if (quantity > 0) {
            const price = competitionData!.overnight[option]?.price || 0;
            const totalPrice = quantity * price;

            totalRevenue += totalPrice;
            totalQuantity += quantity;

            rows.push(`
              <tr>
                <td style="border: 1px solid #ccc; padding: 8px;">${booking.clubName}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${getOvernightLabel(option)}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${quantity}</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${price.toFixed(2)} €</td>
                <td style="border: 1px solid #ccc; padding: 8px;">${totalPrice.toFixed(2)} €</td>
              </tr>
            `);
          }
        });
      });

      return rows.join('');
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Übernachtung Buchungen - ${competitionData!.name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; border-bottom: 2px solid #1976d2; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background-color: #f5f5f5; border: 1px solid #ccc; padding: 10px; text-align: left; }
          td { border: 1px solid #ccc; padding: 8px; vertical-align: top; }
          .summary { background-color: #f9f9f9; padding: 15px; margin: 20px 0; border-left: 4px solid #1976d2; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>🌙 Übernachtung Buchungen</h1>
        <p><strong>Wettkampf:</strong> ${competitionData!.name}</p>
        <p><strong>Exportiert am:</strong> ${new Date().toLocaleDateString('de-DE')}</p>

        <table>
          <thead>
            <tr>
              <th>Verein</th>
              <th>Option</th>
              <th>Anzahl</th>
              <th>Einzelpreis</th>
              <th>Gesamtpreis</th>
            </tr>
          </thead>
          <tbody>
            ${createOvernightRows()}
          </tbody>
        </table>

        <div class="summary">
          <strong>Zusammenfassung:</strong><br>
          Gesamtanzahl Übernachtungen: ${totalQuantity}<br>
          Gesamtumsatz: ${totalRevenue.toFixed(2)} €
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!competitionData) {
    return <Alert severity="info">Keine Wettbewerbsdaten verfügbar.</Alert>;
  }

  // Calculate totals for summary view
  const calculateTShirtSummary = (): TShirtSummary => {
    const summary: TShirtSummary = {
      standard: {},
      individualized: {}
    };
    
    tShirtSizes.forEach(size => {
      summary.standard[size] = 0;
      summary.individualized[size] = 0;
    });
    
    clubBookings.forEach(booking => {
      if (!booking.tShirtBooking) return;
      
      Object.entries(booking.tShirtBooking.sizes).forEach(([size, quantity]) => {
        if (quantity > 0) {
          if (booking.tShirtBooking.individualizable) {
            summary.individualized[size] = (summary.individualized[size] || 0) + quantity;
          } else {
            summary.standard[size] = (summary.standard[size] || 0) + quantity;
          }
        }
      });
    });
    
    return summary;
  };

  const calculateCateringSummary = (): CateringSummary => {
    const summary: CateringSummary = {
      dinnerFriday: 0,
      dinnerFridayVeg: 0,
      breakfastSaturday: 0,
      breakfastSunday: 0
    };
    
    clubBookings.forEach(booking => {
      if (!booking.cateringBooking) return;
      
      Object.entries(booking.cateringBooking).forEach(([option, quantity]) => {
        if (quantity > 0) {
          summary[option] = (summary[option] || 0) + quantity;
        }
      });
    });
    
    return summary;
  };

  const calculateOvernightSummary = (): OvernightSummary => {
    const summary: OvernightSummary = {
      fridayToSaturday: 0,
      saturdayToSunday: 0
    };

    clubBookings.forEach(booking => {
      if (!booking.overnightBooking) return;

      Object.entries(booking.overnightBooking).forEach(([option, quantity]) => {
        if (quantity > 0) {
          summary[option] = (summary[option] || 0) + quantity;
        }
      });
    });

    return summary;
  };

  const tShirtSummary = calculateTShirtSummary();
  const cateringSummary = calculateCateringSummary();
  const overnightSummary = calculateOvernightSummary();

  // Calculate revenue
  const calculateTShirtRevenue = (summary: TShirtSummary): number => {
    let revenue = 0;
    
    Object.values(summary.standard).forEach(quantity => {
      revenue += quantity * competitionData.tShirt.priceStandard;
    });
    
    Object.values(summary.individualized).forEach(quantity => {
      revenue += quantity * competitionData.tShirt.priceIndividualized;
    });
    
    return revenue;
  };

  const calculateCateringRevenue = (summary: CateringSummary): number => {
    let revenue = 0;
    
    Object.entries(summary).forEach(([option, quantity]) => {
      if (competitionData.catering[option] && quantity > 0) {
        revenue += quantity * competitionData.catering[option].price;
      }
    });
    
    return revenue;
  };

  const calculateOvernightRevenue = (summary: OvernightSummary): number => {
    let revenue = 0;

    Object.entries(summary).forEach(([option, quantity]) => {
      if (competitionData.overnight[option] && quantity > 0) {
        revenue += quantity * competitionData.overnight[option].price;
      }
    });

    return revenue;
  };

  const tShirtRevenue = calculateTShirtRevenue(tShirtSummary);
  const cateringRevenue = calculateCateringRevenue(cateringSummary);
  const overnightRevenue = calculateOvernightRevenue(overnightSummary);
  const totalRevenue = tShirtRevenue + cateringRevenue + overnightRevenue;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Zusatzbuchungen
        </Typography>
        
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={exportTShirtsToPrint}
            size="small"
          >
            T-Shirts drucken
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportTShirtsToCSV}
            size="small"
          >
            T-Shirts CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={exportCateringToPrint}
            size="small"
          >
            Verpflegung drucken
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportCateringToCSV}
            size="small"
          >
            Verpflegung CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={exportOvernightToPrint}
            size="small"
          >
            Übernachtung drucken
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportOvernightToCSV}
            size="small"
          >
            Übernachtung CSV
          </Button>
        </Stack>
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Gesamtübersicht" />
          <Tab label="Vereinsübersicht" />
        </Tabs>
      </Box>
      
      {tabValue === 0 ? (
        // Summary view (aggregated data)
        <Box>
          {/* T-Shirt Summary */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                <strong>👕 T-Shirts</strong> - Gesamtumsatz: {tShirtRevenue.toFixed(2)} €
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Größe</TableCell>
                      <TableCell align="right">Standard ({competitionData.tShirt.priceStandard.toFixed(2)} €)</TableCell>
                      <TableCell align="right">Individualisiert ({competitionData.tShirt.priceIndividualized.toFixed(2)} €)</TableCell>
                      <TableCell align="right">Gesamt</TableCell>
                      <TableCell align="right">Umsatz</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tShirtSizes.map(size => {
                      const standardQty = tShirtSummary.standard[size] || 0;
                      const individualQty = tShirtSummary.individualized[size] || 0;
                      const totalQty = standardQty + individualQty;
                      
                      if (totalQty === 0) return null;
                      
                      const revenue = (standardQty * competitionData.tShirt.priceStandard) + 
                                     (individualQty * competitionData.tShirt.priceIndividualized);
                      
                      return (
                        <TableRow key={size}>
                          <TableCell>{size}</TableCell>
                          <TableCell align="right">{standardQty}</TableCell>
                          <TableCell align="right">{individualQty}</TableCell>
                          <TableCell align="right">{totalQty}</TableCell>
                          <TableCell align="right">{revenue.toFixed(2)} €</TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Total row */}
                    <TableRow>
                      <TableCell><strong>Summe</strong></TableCell>
                      <TableCell align="right"><strong>{Object.values(tShirtSummary.standard).reduce((sum, qty) => sum + qty, 0)}</strong></TableCell>
                      <TableCell align="right"><strong>{Object.values(tShirtSummary.individualized).reduce((sum, qty) => sum + qty, 0)}</strong></TableCell>
                      <TableCell align="right"><strong>{
                        Object.values(tShirtSummary.standard).reduce((sum, qty) => sum + qty, 0) +
                        Object.values(tShirtSummary.individualized).reduce((sum, qty) => sum + qty, 0)
                      }</strong></TableCell>
                      <TableCell align="right"><strong>{tShirtRevenue.toFixed(2)} €</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
          
          {/* Catering Summary */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                <strong>🍽️ Verpflegung</strong> - Gesamtumsatz: {cateringRevenue.toFixed(2)} €
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Option</TableCell>
                      <TableCell align="right">Preis pro Einheit</TableCell>
                      <TableCell align="right">Anzahl</TableCell>
                      <TableCell align="right">Umsatz</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(cateringSummary).map(([option, quantity]) => {
                      if (quantity === 0) return null;
                      
                      const price = competitionData.catering[option]?.price || 0;
                      const revenue = quantity * price;
                      
                      return (
                        <TableRow key={option}>
                          <TableCell>{getCateringLabel(option)}</TableCell>
                          <TableCell align="right">{price.toFixed(2)} €</TableCell>
                          <TableCell align="right">{quantity}</TableCell>
                          <TableCell align="right">{revenue.toFixed(2)} €</TableCell>
                        </TableRow>
                      );
                    })}
                    
                    {/* Total row */}
                    <TableRow>
                      <TableCell><strong>Summe</strong></TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right"><strong>{Object.values(cateringSummary).reduce((sum, qty) => sum + qty, 0)}</strong></TableCell>
                      <TableCell align="right"><strong>{cateringRevenue.toFixed(2)} €</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
          
          {/* Übernachtung Summary */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                <strong>🌙 Übernachtung</strong> - Gesamtumsatz: {overnightRevenue.toFixed(2)} €
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Option</TableCell>
                      <TableCell align="right">Preis pro Person</TableCell>
                      <TableCell align="right">Anzahl</TableCell>
                      <TableCell align="right">Umsatz</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(overnightSummary).map(([option, quantity]) => {
                      if (quantity === 0) return null;

                      const price = competitionData.overnight[option]?.price || 0;
                      const revenue = quantity * price;

                      return (
                        <TableRow key={option}>
                          <TableCell>{getOvernightLabel(option)}</TableCell>
                          <TableCell align="right">{price.toFixed(2)} €</TableCell>
                          <TableCell align="right">{quantity}</TableCell>
                          <TableCell align="right">{revenue.toFixed(2)} €</TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Total row */}
                    <TableRow>
                      <TableCell><strong>Summe</strong></TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right"><strong>{Object.values(overnightSummary).reduce((sum, qty) => sum + qty, 0)}</strong></TableCell>
                      <TableCell align="right"><strong>{overnightRevenue.toFixed(2)} €</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          <Paper sx={{ p: 2, mt: 2, bgcolor: '#f9f9f9' }}>
            <Typography variant="h6">Gesamtumsatz Zusatzbuchungen: {totalRevenue.toFixed(2)} €</Typography>
          </Paper>
        </Box>
      ) : (
        // Club-by-club detailed view
        <Box>
          {clubBookings.length === 0 ? (
            <Alert severity="info">Keine Zusatzbuchungen vorhanden.</Alert>
          ) : (
            clubBookings.map((booking, index) => {
              const hasTShirts = booking.tShirtBooking && 
                Object.values(booking.tShirtBooking.sizes).some(qty => qty > 0);
              
              const hasCatering = booking.cateringBooking &&
                Object.values(booking.cateringBooking).some(qty => qty > 0);

              const hasOvernight = booking.overnightBooking &&
                Object.values(booking.overnightBooking).some(qty => qty > 0);

              if (!hasTShirts && !hasCatering && !hasOvernight) return null;
              
              return (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{booking.clubName}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* T-Shirts */}
                    {hasTShirts && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>👕 T-Shirts</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Größe</TableCell>
                                <TableCell align="right">Anzahl</TableCell>
                                <TableCell align="right">Einzelpreis</TableCell>
                                <TableCell align="right">Gesamtpreis</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(booking.tShirtBooking.sizes).map(([size, quantity]) => {
                                if (quantity === 0) return null;
                                
                                const pricePerShirt = booking.tShirtBooking.individualizable
                                  ? competitionData.tShirt.priceIndividualized
                                  : competitionData.tShirt.priceStandard;
                                
                                const totalPrice = quantity * pricePerShirt;
                                
                                return (
                                  <TableRow key={size}>
                                    <TableCell>{size}</TableCell>
                                    <TableCell align="right">{quantity}</TableCell>
                                    <TableCell align="right">{pricePerShirt.toFixed(2)} €</TableCell>
                                    <TableCell align="right">{totalPrice.toFixed(2)} €</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        {booking.tShirtBooking.individualizable && booking.tShirtBooking.customText && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Individualisierung:</strong> {booking.tShirtBooking.customText}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Catering */}
                    {hasCatering && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>🍽️ Verpflegung</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Option</TableCell>
                                <TableCell align="right">Anzahl</TableCell>
                                <TableCell align="right">Einzelpreis</TableCell>
                                <TableCell align="right">Gesamtpreis</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(booking.cateringBooking).map(([option, quantity]) => {
                                if (quantity === 0) return null;
                                
                                const price = competitionData.catering[option]?.price || 0;
                                const totalPrice = quantity * price;
                                
                                return (
                                  <TableRow key={option}>
                                    <TableCell>{getCateringLabel(option)}</TableCell>
                                    <TableCell align="right">{quantity}</TableCell>
                                    <TableCell align="right">{price.toFixed(2)} €</TableCell>
                                    <TableCell align="right">{totalPrice.toFixed(2)} €</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* Übernachtung */}
                    {hasOvernight && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>🌙 Übernachtung</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Option</TableCell>
                                <TableCell align="right">Anzahl</TableCell>
                                <TableCell align="right">Einzelpreis</TableCell>
                                <TableCell align="right">Gesamtpreis</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(booking.overnightBooking).map(([option, quantity]) => {
                                if (quantity === 0) return null;

                                const price = competitionData.overnight[option]?.price || 0;
                                const totalPrice = quantity * price;

                                return (
                                  <TableRow key={option}>
                                    <TableCell>{getOvernightLabel(option)}</TableCell>
                                    <TableCell align="right">{quantity}</TableCell>
                                    <TableCell align="right">{price.toFixed(2)} €</TableCell>
                                    <TableCell align="right">{totalPrice.toFixed(2)} €</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
};

export default AdditionalServicesOverview;